import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import userService from '../../utils/userService';
import applicationService from '../../utils/applicationService';
import missionService from '../../utils/missionService';
import authService from '../../utils/authService';

// Actions asynchrones pour récupérer les vraies données
export const fetchUserStats = createAsyncThunk(
  'professionalDashboard/fetchUserStats',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserStatsById(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des statistiques');
    }
  }
);

export const fetchUserApplications = createAsyncThunk(
  'professionalDashboard/fetchUserApplications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await applicationService.getMyApplications();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des candidatures');
    }
  }
);

export const fetchUserMissions = createAsyncThunk(
  'professionalDashboard/fetchUserMissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await missionService.getUserMissions();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des missions');
    }
  }
);

export const fetchDashboardData = createAsyncThunk(
  'professionalDashboard/fetchDashboardData',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const userId = auth.user?.id || auth.user?._id;
      
      if (!userId) {
        throw new Error('Utilisateur non connecté');
      }

      // Récupération parallèle de toutes les données
      const [statsResult, applicationsResult, missionsResult] = await Promise.allSettled([
        dispatch(fetchUserStats(userId)).unwrap(),
        dispatch(fetchUserApplications()).unwrap(),
        dispatch(fetchUserMissions()).unwrap()
      ]);

      return {
        stats: statsResult.status === 'fulfilled' ? statsResult.value : null,
        applications: applicationsResult.status === 'fulfilled' ? applicationsResult.value : [],
        missions: missionsResult.status === 'fulfilled' ? missionsResult.value : []
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Erreur lors de la récupération des données du tableau de bord');
    }
  }
);

const initialState = {
  user: authService.getCurrentUser() || null,
  stats: {
    missionsApplied: 0,
    missionsInProgress: 0,
    missionsCompleted: 0,
    estimatedRevenue: 0
  },
  missions: [],
  applications: [],
  notifications: [],
  messages: [],
  revenue: {
    monthlyData: [],
    totalEarned: 0,
    currentMonth: 0,
    pendingPayments: 0
  },
  loading: false,
  error: null,
  filters: {
    missionStatus: 'all' // all, applied, in_progress, completed
  },
  unreadNotifications: 0,
  unreadMessages: 0
};

const professionalDashboardSlice = createSlice({
  name: 'professionalDashboard',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    setMissionFilter: (state, action) => {
      state.filters.missionStatus = action.payload;
    },
    markNotificationAsRead: (state, action) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification) {
        notification.read = true;
        state.unreadNotifications = Math.max(0, state.unreadNotifications - 1);
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true;
      });
      state.unreadNotifications = 0;
    },
    markMessageAsRead: (state, action) => {
      const message = state.messages.find(m => m.id === action.payload);
      if (message && message.unread) {
        message.unread = false;
        state.unreadMessages = Math.max(0, state.unreadMessages - 1);
      }
    },
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadNotifications += 1;
      }
    },
    updateStats: (state, action) => {
      state.stats = { ...state.stats, ...action.payload };
    },
    addMission: (state, action) => {
      state.missions.unshift(action.payload);
    },
    updateMissionStatus: (state, action) => {
      const { missionId, status } = action.payload;
      const mission = state.missions.find(m => m.id === missionId);
      if (mission) {
        mission.status = status;
        
        // Mise à jour des statistiques
        const oldStatus = mission.status;
        if (oldStatus === 'applied' && status === 'in_progress') {
          state.stats.missionsInProgress += 1;
        } else if (oldStatus === 'in_progress' && status === 'completed') {
          state.stats.missionsInProgress -= 1;
          state.stats.missionsCompleted += 1;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserStats
      .addCase(fetchUserStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          // Adaptation des données de l'API aux statistiques du tableau de bord
          state.stats = {
            missionsApplied: action.payload.applicationsSent || 0,
            missionsInProgress: action.payload.missionsInProgress || 0,
            missionsCompleted: action.payload.missionsCompleted || 0,
            estimatedRevenue: action.payload.totalEarnings || 0
          };
        }
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchUserApplications
      .addCase(fetchUserApplications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload || [];
      })
      .addCase(fetchUserApplications.rejected, (state, action) => {
        state.loading = false;
      })
      
      // fetchUserMissions
      .addCase(fetchUserMissions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserMissions.fulfilled, (state, action) => {
        state.loading = false;
        state.missions = action.payload || [];
      })
      .addCase(fetchUserMissions.rejected, (state, action) => {
        state.loading = false;
      })
      
      // fetchDashboardData
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        const { stats, applications, missions } = action.payload;
        state.error = null;
        
        if (stats) {
          state.stats = {
            missionsApplied: stats.applicationsSent || 0,
            missionsInProgress: stats.missionsInProgress || 0,
            missionsCompleted: stats.missionsCompleted || 0,
            estimatedRevenue: stats.totalEarnings || 0
          };
        }
        
        if (applications) {
          state.applications = applications;
        }
        
        if (missions) {
          state.missions = missions;
        }
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Sélecteurs
export const selectFilteredMissions = (state) => {
  const { missions, filters } = state.professionalDashboard;
  if (filters.missionStatus === 'all') {
    return missions;
  }
  return missions.filter(mission => mission.status === filters.missionStatus);
};

export const selectUnreadNotifications = (state) => {
  return state.professionalDashboard.unreadNotifications;
};

export const selectUnreadMessages = (state) => {
  return state.professionalDashboard.unreadMessages;
};

export const selectRecentNotifications = (state) => {
  return state.professionalDashboard.notifications.slice(0, 5);
};

export const selectRecentMessages = (state) => {
  return state.professionalDashboard.messages.slice(0, 5);
};

export const {
  setLoading,
  setError,
  updateUser,
  setMissionFilter,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  markMessageAsRead,
  addNotification,
  updateStats,
  addMission,
  updateMissionStatus
} = professionalDashboardSlice.actions;

export default professionalDashboardSlice.reducer;